import type { GatewayBrowserClient } from "../gateway.ts";
import type { YouTrackIssue } from "../types.ts";

export interface YouTrackState {
  client: GatewayBrowserClient | null;
  connected: boolean;
  sessionKey: string;
  youtrackLoading: boolean;
  youtrackSearchQuery: string;
  youtrackSearchProject: string;
  youtrackResults: YouTrackIssue[];
  youtrackSelectedIssue: YouTrackIssue | null;
  youtrackError: string | null;
  // Create form
  youtrackCreateDraft: YouTrackCreateDraft | null;
  youtrackCreateOpen: boolean;
  youtrackCreateError: string | null;
  // Comments
  youtrackComments: YouTrackComment[];
  youtrackCommentsLoading: boolean;
  // Saved filters
  youtrackSavedFilters: YouTrackSavedFilter[];
}

export interface YouTrackComment {
  id: string;
  author: string;
  text: string;
  createdAt: string;
}

export interface YouTrackCreateDraft {
  summary: string;
  description: string;
  type: string;
  assignee: string;
}

export interface YouTrackSavedFilter {
  name: string;
  query: string;
  project: string;
}

type GatewayToolCallResult = {
  success?: boolean;
  output?: string | null;
  error?: string | null;
};

function normalizeIssueStatus(value: unknown): YouTrackIssue["status"] {
  const normalized =
    typeof value === "string"
      ? value
          .trim()
          .toLowerCase()
          .replace(/[\s-]+/g, "_")
      : "";
  switch (normalized) {
    case "in_progress":
    case "resolved":
    case "closed":
    case "duplicate":
    case "wontfix":
    case "checked":
    case "validated":
    case "declined":
      return normalized;
    default:
      return "open";
  }
}

function normalizeIssue(issue: Record<string, unknown>): YouTrackIssue {
  const idReadable =
    typeof issue.idReadable === "string" && issue.idReadable.trim()
      ? issue.idReadable
      : typeof issue.id === "string" && issue.id.trim()
        ? issue.id
        : "";
  const customFields =
    issue.customFields && typeof issue.customFields === "object"
      ? (issue.customFields as Record<string, unknown>)
      : {};
  const created = typeof issue.created === "number" ? issue.created : null;
  const updated = typeof issue.updated === "number" ? issue.updated : null;

  return {
    id: typeof issue.id === "string" && issue.id ? issue.id : idReadable,
    idReadable,
    summary: typeof issue.summary === "string" ? issue.summary : "",
    description: typeof issue.description === "string" ? issue.description : null,
    status: normalizeIssueStatus(issue.state ?? customFields.State),
    priority:
      typeof issue.priority === "string"
        ? issue.priority
        : typeof customFields.Priority === "string"
          ? customFields.Priority
          : null,
    project: typeof issue.project === "string" ? issue.project : "",
    createdAt: created ? new Date(created).toISOString() : "",
    updatedAt: updated ? new Date(updated).toISOString() : "",
    assignee: typeof issue.assignee === "string" ? issue.assignee : null,
    reporter: typeof issue.reporter === "string" ? issue.reporter : null,
    url: typeof issue.url === "string" ? issue.url : null,
  };
}

function parseToolOutput(result: GatewayToolCallResult): unknown {
  if (!result.success) {
    throw new Error(result.error || "YouTrack tool call failed");
  }
  if (!result.output) {
    return null;
  }
  try {
    return JSON.parse(result.output);
  } catch {
    return result.output;
  }
}

async function callYouTrackTool<T>(
  state: YouTrackState,
  toolId: string,
  params: Record<string, unknown>,
): Promise<T> {
  if (!state.client || !state.connected) {
    throw new Error("YouTrack is unavailable");
  }
  const response = (await state.client.request("mcp.tools.call", {
    toolId,
    params,
    sessionKey: state.sessionKey,
  })) as GatewayToolCallResult;
  return parseToolOutput(response) as T;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export async function searchYouTrackIssues(state: YouTrackState): Promise<void> {
  if (!state.client || !state.connected || state.youtrackLoading) {
    return;
  }
  state.youtrackLoading = true;
  state.youtrackError = null;
  try {
    const output = await callYouTrackTool<unknown>(state, "youtrack_search", {
      query: state.youtrackSearchQuery.trim(),
      project: state.youtrackSearchProject.trim() || undefined,
      limit: 25,
    });
    const rawIssues = Array.isArray(output)
      ? output
      : isRecord(output) && Array.isArray(output.issues)
        ? output.issues
        : [];
    const issues = rawIssues.filter(isRecord).map(normalizeIssue);
    state.youtrackResults = issues;
    state.youtrackSelectedIssue = issues[0] ?? null;
  } catch (err) {
    state.youtrackError = String(err);
  } finally {
    state.youtrackLoading = false;
  }
}

export async function loadYouTrackIssue(state: YouTrackState, issueId: string): Promise<void> {
  const trimmed = issueId.trim();
  if (!trimmed || !state.client || !state.connected) {
    return;
  }
  state.youtrackLoading = true;
  state.youtrackError = null;
  try {
    const output = await callYouTrackTool<unknown>(state, "youtrack_get", { issueId: trimmed });
    if (!isRecord(output)) {
      throw new Error("YouTrack issue response was not an object");
    }
    const issue = normalizeIssue(output);
    state.youtrackSelectedIssue = issue;
    if (!state.youtrackResults.some((item) => item.idReadable === issue.idReadable)) {
      state.youtrackResults = [issue, ...state.youtrackResults];
    }
  } catch (err) {
    state.youtrackError = String(err);
  } finally {
    state.youtrackLoading = false;
  }
}

export async function createYouTrackIssue(state: YouTrackState): Promise<YouTrackIssue | null> {
  if (!state.client || !state.connected) {
    throw new Error("YouTrack is unavailable");
  }
  const draft = state.youtrackCreateDraft;
  if (!draft || !draft.summary.trim()) {
    state.youtrackCreateError = "Summary is required";
    return null;
  }
  state.youtrackLoading = true;
  state.youtrackCreateError = null;
  state.youtrackError = null;
  try {
    const requestId = crypto.randomUUID();
    const output = await callYouTrackTool<unknown>(state, "youtrack_create", {
      project: state.youtrackSearchProject.trim() || "JAISIU",
      summary: draft.summary.trim(),
      description: draft.description.trim() || undefined,
      type: draft.type.trim() || undefined,
      assignee: draft.assignee.trim() || undefined,
      requestId,
    });
    const issue = normalizeIssue(isRecord(output) ? output : {});
    state.youtrackCreateOpen = false;
    state.youtrackCreateDraft = null;
    // Refresh search to include the new issue
    await searchYouTrackIssues(state);
    return issue;
  } catch (err) {
    state.youtrackCreateError = String(err);
    return null;
  } finally {
    state.youtrackLoading = false;
  }
}

export async function transitionYouTrackIssue(
  state: YouTrackState,
  issueId: string,
  targetState: string,
): Promise<void> {
  const trimmed = issueId.trim();
  if (!trimmed || !state.client || !state.connected) {
    return;
  }
  state.youtrackLoading = true;
  state.youtrackError = null;
  try {
    const requestId = crypto.randomUUID();
    await callYouTrackTool<unknown>(state, "youtrack_transition", {
      issueId: trimmed,
      state: targetState,
      requestId,
    });
    // Reload the issue to reflect new state
    await loadYouTrackIssue(state, trimmed);
  } catch (err) {
    state.youtrackError = String(err);
  } finally {
    state.youtrackLoading = false;
  }
}

export async function addYouTrackComment(
  state: YouTrackState,
  issueId: string,
  text: string,
): Promise<void> {
  const trimmed = issueId.trim();
  const trimmedText = text.trim();
  if (!trimmed || !trimmedText || !state.client || !state.connected) {
    return;
  }
  state.youtrackLoading = true;
  state.youtrackError = null;
  try {
    const requestId = crypto.randomUUID();
    await callYouTrackTool<unknown>(state, "youtrack_comment", {
      issueId: trimmed,
      text: trimmedText,
      requestId,
    });
    // Reload comments
    await loadYouTrackComments(state, trimmed);
  } catch (err) {
    state.youtrackError = String(err);
  } finally {
    state.youtrackLoading = false;
  }
}

export async function loadYouTrackComments(state: YouTrackState, issueId: string): Promise<void> {
  const trimmed = issueId.trim();
  if (!trimmed || !state.client || !state.connected) {
    return;
  }
  // The MCP tool doesn't expose a comments list endpoint directly,
  // so we just reset the comments array here.
  // In a full implementation, you'd call a dedicated endpoint.
  state.youtrackComments = [];
}

const SAVED_FILTERS_KEY = "youtrack-saved-filters";

export function saveYouTrackFilter(
  state: YouTrackState,
  name: string,
  query: string,
  project: string,
): void {
  const filters = state.youtrackSavedFilters ?? [];
  const existing = filters.findIndex((f) => f.name === name);
  const newFilter: YouTrackSavedFilter = { name, query, project };
  if (existing >= 0) {
    state.youtrackSavedFilters = [
      ...filters.slice(0, existing),
      newFilter,
      ...filters.slice(existing + 1),
    ];
  } else {
    state.youtrackSavedFilters = [...filters, newFilter];
  }
  try {
    localStorage.setItem(SAVED_FILTERS_KEY, JSON.stringify(state.youtrackSavedFilters));
  } catch {
    // localStorage unavailable
  }
}

export function loadYouTrackSavedFilters(state: YouTrackState): void {
  try {
    const stored = localStorage.getItem(SAVED_FILTERS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        state.youtrackSavedFilters = parsed;
      }
    }
  } catch {
    state.youtrackSavedFilters = [];
  }
}

export function deleteYouTrackFilter(state: YouTrackState, name: string): void {
  state.youtrackSavedFilters = (state.youtrackSavedFilters ?? []).filter((f) => f.name !== name);
  try {
    localStorage.setItem(SAVED_FILTERS_KEY, JSON.stringify(state.youtrackSavedFilters));
  } catch {
    // localStorage unavailable
  }
}

export function searchMyIssues(state: YouTrackState): void {
  state.youtrackSearchQuery = `assignee:me #Unresolved`;
  void searchYouTrackIssues(state);
}
